import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Grid,
  Card,
  Heading,
  Text,
  Button,
  Layer,
  ResponsiveContext,
} from "grommet";
import {
  DocumentUser,
  Logout,
  Alert,
  Lock,
  MailOption,
  StatusCritical,
  StatusWarning,
  StatusInfo,
  StatusGood,
  Refresh,
  LinkNext,
  Close,
  Checkmark,
} from "grommet-icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  HotelBriefService,
  HomeStatsService,
  PasscodeService,
} from "../../services";
import HotelService from "../../services/hotel.service";
import { presentToastErrorContent, presentToastSuccess } from "@shared/Toast";
import { useFetchHotel } from "./redux/hooks";
import { useSetCurrentUser } from "./redux/setCurrentUser";

const BRAND = "#0955b8";
const BRAND_TINT = "#e9f0fb";

const SEVERITY = {
  info: { color: "#0095ff", tint: "#e7f3ff", Icon: StatusInfo },
  warning: { color: "#e0940a", tint: "#fff5e2", Icon: StatusWarning },
  critical: { color: "#ff4040", tint: "#ffecec", Icon: StatusCritical },
};

function formatTime(iso) {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatResponseTime(minutes, t) {
  if (minutes == null) return null;
  if (minutes < 60) return t("home.avgResponseTimeMinutes", { count: minutes });
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins
    ? t("home.avgResponseTimeHoursMinutes", { hours, minutes: mins })
    : t("home.avgResponseTimeHours", { count: hours });
}

// A round, tinted icon badge — the little touch that makes tiles feel designed.
function IconBadge({ Icon, color, tint, size = "20px" }) {
  return (
    <Box
      background={tint}
      round="full"
      align="center"
      justify="center"
      pad="xsmall"
      flex={false}
    >
      <Icon color={color} size={size} />
    </Box>
  );
}

// Pulsing skeleton block (see .loxe-skeleton in global.scss).
function Skel({ width, height = "14px", round = "4px" }) {
  return (
    <Box
      className="loxe-skeleton"
      width={width}
      height={height}
      round={round}
      flex={false}
    />
  );
}

// Skeleton placeholder for the brief list while it (or the hotel) loads.
function SkeletonBriefList({ rows = 4 }) {
  return (
    <Card background="white" elevation="xsmall" round="small" flex={false}>
      {Array.from({ length: rows }).map((_, i) => (
        <Box
          key={i}
          direction="row"
          align="center"
          gap="small"
          pad={{ horizontal: "medium", vertical: "small" }}
          border={
            i < rows - 1 ? { side: "bottom", color: "#eef0f2" } : undefined
          }
          flex={false}
        >
          <Skel width="28px" height="28px" round="full" />
          <Box flex gap="xsmall">
            <Skel width="45%" height="12px" />
            <Skel width="80%" height="10px" />
          </Box>
          <Skel width="18px" height="18px" round="full" />
        </Box>
      ))}
    </Card>
  );
}

function StatCard({ Icon, label, value, sub, subColor, loading, onClick }) {
  return (
    <Card
      background="white"
      elevation="xsmall"
      round="small"
      pad="medium"
      flex={false}
      onClick={onClick}
      hoverIndicator={onClick ? { background: "#eaf1fb" } : false}
      focusIndicator={false}
      style={onClick ? { cursor: "pointer" } : undefined}
    >
      <Box direction="row" align="start" justify="between" gap="small">
        <Box gap="xxsmall">
          <Text size="small" color="text-xweak">
            {label}
          </Text>
          {loading ? (
            <Box pad={{ vertical: "6px" }}>
              <Skel width="56px" height="24px" />
            </Box>
          ) : (
            <Text size="xxlarge" weight="bold" color="text-strong">
              {value}
            </Text>
          )}
        </Box>
        <IconBadge Icon={Icon} color={BRAND} tint={BRAND_TINT} />
      </Box>
      {!loading && sub && (
        <Text
          size="xsmall"
          weight="bold"
          color={subColor || "#d98500"}
          margin={{ top: "xsmall" }}
        >
          {sub}
        </Text>
      )}
    </Card>
  );
}

export default function AIBriefPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hotel, fetchHotel } = useFetchHotel();
  const { isAdmin } = useSetCurrentUser();
  const size = React.useContext(ResponsiveContext);
  const isMobile = size === "small";
  const [enabling, setEnabling] = useState(false);
  const [showUnread, setShowUnread] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  const [showTrainingPrompt, setShowTrainingPrompt] = useState(false);
  const [briefs, setBriefs] = useState([]);
  const [loadingBriefs, setLoadingBriefs] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [stats, setStats] = useState(null);
  const [activePasscodes, setActivePasscodes] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);

  const unreadMessages = stats?.unreadMessagesList || [];
  const openMessage = (n) => {
    setShowUnread(false);
    // Coerce to an id string — never render an object into the URL.
    const id = n.booking?._id || n.booking;
    if (id) navigate(`/guests/${id}/messages`);
  };
  const verifyList = stats?.checkinsToVerifyList || [];
  const openVerify = (b) => {
    setShowVerify(false);
    navigate(`/guests/${b.id}`);
  };

  const loadBriefs = useCallback(async () => {
    if (!hotel.aiInsightsEnabled) {
      setLoadingBriefs(false);
      return;
    }
    setLoadingBriefs(true);
    try {
      const res = await HotelBriefService.getBriefs();
      setBriefs(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      presentToastErrorContent(err);
    } finally {
      setLoadingBriefs(false);
    }
  }, [hotel.aiInsightsEnabled]);

  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const [statsRes, passcodesRes] = await Promise.all([
        HomeStatsService.getStats(),
        hotel.serviceType === "messaging" ? Promise.resolve({ data: [] }) : PasscodeService.current(),
      ]);
      setStats(statsRes.data || {});
      setActivePasscodes(
        Array.isArray(passcodesRes.data) ? passcodesRes.data.length : 0,
      );
    } catch (err) {
      presentToastErrorContent(err);
    } finally {
      setLoadingStats(false);
    }
  }, [hotel.serviceType]);

  useEffect(() => {
    loadBriefs();
  }, [loadBriefs]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const refreshAll = () => {
    loadBriefs();
    loadStats();
  };

  const enableAiInsights = async () => {
    setEnabling(true);
    try {
      await HotelService.updateHotel({ aiInsightsEnabled: true });
      await fetchHotel();
      presentToastSuccess(t("aiBrief.enabledToast", "AI Brief enabled"));
      loadBriefs();
    } catch (err) {
      presentToastErrorContent(err);
    } finally {
      setEnabling(false);
    }
  };

  const handleUpdateStatus = async (brief, status) => {
    setUpdatingId(brief._id);
    try {
      await HotelBriefService.updateStatus(brief._id, status);
      setBriefs((prev) => prev.filter((b) => b._id !== brief._id));
    } catch (err) {
      presentToastErrorContent(err);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleAcceptTraining = async (brief) => {
    setUpdatingId(brief._id);
    try {
      await HotelBriefService.acceptTrainingSuggestion(brief._id);
      setBriefs((prev) => prev.filter((b) => b._id !== brief._id));
      presentToastSuccess(
        t("aiBrief.trainingAccepted", "Added to your AI training data"),
      );
      setShowTrainingPrompt(true);
    } catch (err) {
      presentToastErrorContent(err);
    } finally {
      setUpdatingId(null);
    }
  };

  const goToTrainingQnA = () => {
    setShowTrainingPrompt(false);
    navigate("/settings/training?tab=qna");
  };

  const statCards = [
    {
      Icon: DocumentUser,
      label: t("home.arrivalsToday"),
      value: stats?.arrivalsToday ?? 0,
      sub:
        hotel.serviceType !== "messaging" && stats?.arrivalsMissingKeys > 0
          ? t("home.withoutKeys", { count: stats.arrivalsMissingKeys })
          : null,
      link: "/guests",
      show: true,
    },
    {
      Icon: Logout,
      label: t("home.departuresToday"),
      value: stats?.departuresToday ?? 0,
      link: "/guests",
      show: true,
    },
    {
      Icon: Alert,
      label: t("home.checkinsToVerify"),
      value: stats?.checkinsToVerify ?? 0,
      onClick: () => setShowVerify(true),
      show: !!hotel.onlineCheckinEnabled,
    },
    {
      Icon: Lock,
      label: t("home.activePasscodes"),
      value: activePasscodes ?? 0,
      link: "/current-passcodes",
      show: hotel.serviceType !== "messaging",
    },
    {
      Icon: MailOption,
      label: t("home.unreadMessages"),
      value: stats?.unreadMessages ?? 0,
      sub: formatResponseTime(stats?.avgResponseTimeMinutes, t),
      subColor: "text-xweak",
      onClick: () => setShowUnread(true),
      show: true,
    },
  ].filter((c) => c.show);

  const columns = isMobile ? ["1fr"] : { count: "fit", size: "220px" };

  return (
    <Box
      pad={{ horizontal: isMobile ? "medium" : "large", vertical: "medium" }}
      fill
      overflow={{ vertical: "auto" }}
      align="center"
    >
      <Box width={{ width: "100%", max: "1180px" }} gap="medium" flex={false}>
        {/* Page header */}
        <Box direction="row" align="center" justify="between" gap="small">
          <Box>
            <Heading level={2} margin="none" size="small">
              {hotel?.name || t("nav.home")}
            </Heading>
            <Text color="text-xweak" size="small">
              {new Date().toLocaleDateString(undefined, {
                weekday: "long",
                month: "long",
                day: "numeric",
              })}
            </Text>
          </Box>
          <Button
            icon={<Refresh size="small" />}
            label={t("aiBrief.refresh", "Refresh")}
            onClick={refreshAll}
            secondary
            size="small"
          />
        </Box>

        {/* Stat tiles */}
        <Grid columns={columns} gap="medium">
          {statCards.map((c) => (
            <StatCard
              key={c.label}
              {...c}
              loading={loadingStats}
              onClick={
                c.onClick || (c.link ? () => navigate(c.link) : undefined)
              }
            />
          ))}
        </Grid>

        {/* AI brief */}
        {!hotel?._id ? (
          <Box gap="small" margin={{ top: "small" }}>
            <Skel width="120px" height="22px" />
            <SkeletonBriefList />
          </Box>
        ) : hotel.aiInsightsEnabled ? (
          <Box gap="small" margin={{ top: "small" }}>
            <Box direction="row" align="center" gap="small">
              <Heading level={3} margin="none">
                {t("aiBrief.title")}
              </Heading>
              {!loadingBriefs && briefs.length > 0 && (
                <Box
                  background={BRAND}
                  round="full"
                  pad={{ horizontal: "xsmall" }}
                  align="center"
                  justify="center"
                >
                  <Text size="xsmall" weight="bold" color="white">
                    {briefs.length}
                  </Text>
                </Box>
              )}
            </Box>

            {loadingBriefs ? (
              <SkeletonBriefList />
            ) : briefs.length === 0 ? (
              <Card
                background="white"
                elevation="xsmall"
                round="small"
                pad="large"
                flex={false}
              >
                <Box align="center" gap="xsmall">
                  <StatusGood color="status-ok" />
                  <Text color="text-weak">{t("aiBrief.empty")}</Text>
                </Box>
              </Card>
            ) : (
              <Card
                background="white"
                elevation="xsmall"
                round="small"
                flex={false}
              >
                {briefs.map((brief, i) => {
                  const sev = SEVERITY[brief.severity] || SEVERITY.info;
                  const clickable = !!brief.link;
                  return (
                    <Box
                      key={brief._id}
                      direction="row"
                      align={isMobile ? "start" : "center"}
                      gap="small"
                      pad={{ horizontal: "medium", vertical: "small" }}
                      border={
                        i < briefs.length - 1
                          ? { side: "bottom", color: "#bdbdbd" }
                          : undefined
                      }
                      onClick={
                        clickable ? () => navigate(brief.link) : undefined
                      }
                      hoverIndicator={
                        clickable ? { background: "#eaf1fb" } : false
                      }
                      focusIndicator={false}
                      style={clickable ? { cursor: "pointer" } : undefined}
                    >
                      <IconBadge
                        Icon={sev.Icon}
                        color={sev.color}
                        tint={sev.tint}
                        size="18px"
                      />
                      <Box flex gap="xxsmall">
                        <Box direction="row" align="center" gap="xsmall">
                          <Text weight="bold" color="text-strong">
                            {brief.title}
                          </Text>
                          {clickable && !isMobile && (
                            <LinkNext size="14px" color="text-xweak" />
                          )}
                          <Box flex />
                          {!isMobile && (
                            <Text size="xsmall" color="text-xweak">
                              {formatTime(brief.lastSeenAt || brief.createdAt)}
                            </Text>
                          )}
                        </Box>
                        <Text size="small" color="text-weak">
                          {brief.message}
                        </Text>
                        {brief.type === "training-data-suggestion" &&
                          brief.context?.question && (
                            <Box
                              margin={{ top: "xsmall" }}
                              gap="xsmall"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Box
                                background="#eceef1"
                                round="xsmall"
                                pad="small"
                                gap="xxsmall"
                                border={{ color: "#c5cad2", size: "1px" }}
                              >
                                <Text size="xsmall" color="text-xweak">
                                  {t("aiBrief.suggestedQuestion", "Question")}
                                </Text>
                                <Text size="small" weight="bold">
                                  {brief.context.question}
                                </Text>
                                <Text
                                  size="xsmall"
                                  color="text-xweak"
                                  margin={{ top: "xsmall" }}
                                >
                                  {t("aiBrief.suggestedAnswer", "Answer")}
                                </Text>
                                <Text size="small">{brief.context.answer}</Text>
                              </Box>
                              <Box direction="row">
                                <Button
                                  size="small"
                                  primary
                                  icon={<Checkmark size="small" />}
                                  label={t(
                                    "aiBrief.acceptTrainingSuggestion",
                                    "Add to training data",
                                  )}
                                  disabled={updatingId === brief._id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAcceptTraining(brief);
                                  }}
                                />
                              </Box>
                              <Text size="xsmall" color="text-xweak">
                                {t(
                                  "aiBrief.acceptTrainingSuggestionHint",
                                  'This will add the Q&A as "Pending" — you\'ll be able to edit it before publishing.',
                                )}
                              </Text>
                            </Box>
                          )}
                        {isMobile && (
                          <Text size="xsmall" color="text-xweak">
                            {formatTime(brief.lastSeenAt || brief.createdAt)}
                          </Text>
                        )}
                      </Box>
                      {/* Single dismiss (X) — clicking the body navigates instead */}
                      <Button
                        plain
                        icon={<Close size="small" color="text-xweak" />}
                        a11yTitle={t("aiBrief.dismiss")}
                        tip={t("aiBrief.dismiss")}
                        disabled={updatingId === brief._id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUpdateStatus(brief, "dismissed");
                        }}
                        hoverIndicator={{ background: "#e6e9ec" }}
                        focusIndicator={false}
                        style={{
                          padding: 6,
                          borderRadius: 999,
                          flex: "0 0 auto",
                        }}
                      />
                    </Box>
                  );
                })}
              </Card>
            )}
          </Box>
        ) : (
          <Card
            background="white"
            elevation="xsmall"
            round="small"
            pad="large"
            flex={false}
            margin={{ top: "small" }}
          >
            <Box gap="small" align="start">
              <Box direction="row" align="center" gap="small">
                <Box
                  background={BRAND}
                  round="full"
                  pad={{ horizontal: "xsmall" }}
                >
                  <Text size="xsmall" weight="bold" color="white">
                    AI
                  </Text>
                </Box>
                <Heading level={3} margin="none">
                  {t("aiBrief.title")}
                </Heading>
              </Box>
              <Text color="text-weak">
                {t(
                  "aiBrief.offDescription",
                  "Turn on AI insights to get a daily brief of what needs your attention — keys not sent, offline locks, unanswered guests and more.",
                )}
              </Text>
              {isAdmin ? (
                <Button
                  primary
                  label={t("aiBrief.enable", "Enable AI Brief")}
                  onClick={enableAiInsights}
                  disabled={enabling}
                />
              ) : (
                <Text size="small" color="text-xweak">
                  {t("aiBrief.askAdmin", "Ask an admin to enable AI insights.")}
                </Text>
              )}
            </Box>
          </Card>
        )}
      </Box>

      {showUnread && (
        <Layer
          onEsc={() => setShowUnread(false)}
          onClickOutside={() => setShowUnread(false)}
          responsive={false}
          margin="medium"
        >
          <Box width={{ width: "92vw", max: "480px" }} pad="medium" gap="small">
            <Box direction="row" align="center" justify="between">
              <Heading level={3} margin="none">
                {t("home.unreadMessages")} ({unreadMessages.length})
              </Heading>
              <Button
                plain
                icon={<Close size="small" color="text-xweak" />}
                a11yTitle={t("common.close", "Close")}
                onClick={() => setShowUnread(false)}
                hoverIndicator={{ background: "#e6e9ec" }}
                focusIndicator={false}
                style={{ padding: 6, borderRadius: 999 }}
              />
            </Box>
            {unreadMessages.length === 0 ? (
              <Box align="center" pad="medium" gap="xsmall">
                <StatusGood color="status-ok" />
                <Text color="text-weak">
                  {t("home.noUnreadMessages", "No unread messages")}
                </Text>
              </Box>
            ) : (
              <Box
                gap="xsmall"
                overflow={{ vertical: "auto" }}
                style={{ maxHeight: "60vh" }}
              >
                {unreadMessages.map((n) => (
                  <Box
                    key={n.id}
                    direction="row"
                    align="center"
                    gap="small"
                    pad="small"
                    round="xsmall"
                    flex={false}
                    onClick={() => openMessage(n)}
                    hoverIndicator={{ background: "#eaf1fb" }}
                    focusIndicator={false}
                    style={{ cursor: "pointer" }}
                  >
                    <IconBadge
                      Icon={MailOption}
                      color={BRAND}
                      tint={BRAND_TINT}
                      size="16px"
                    />
                    <Box flex>
                      <Text weight="bold" size="small" color="text-strong">
                        {n.guestName || t("guestDetails.guest", "Guest")}
                        {n.roomNumber
                          ? ` · ${t("dashboard.room", { number: n.roomNumber })}`
                          : ""}
                      </Text>
                      <Text size="small" color="text-weak" truncate>
                        {n.text}
                      </Text>
                    </Box>
                    <LinkNext size="14px" color="text-xweak" />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Layer>
      )}

      {showVerify && (
        <Layer
          onEsc={() => setShowVerify(false)}
          onClickOutside={() => setShowVerify(false)}
          responsive={false}
          margin="medium"
        >
          <Box width={{ width: "92vw", max: "480px" }} pad="medium" gap="small">
            <Box direction="row" align="center" justify="between">
              <Heading level={3} margin="none">
                {t("home.checkinsToVerify")} ({verifyList.length})
              </Heading>
              <Button
                plain
                icon={<Close size="small" color="text-xweak" />}
                a11yTitle={t("common.close", "Close")}
                onClick={() => setShowVerify(false)}
                hoverIndicator={{ background: "#e6e9ec" }}
                focusIndicator={false}
                style={{ padding: 6, borderRadius: 999 }}
              />
            </Box>
            {verifyList.length === 0 ? (
              <Box align="center" pad="medium" gap="xsmall">
                <StatusGood color="status-ok" />
                <Text color="text-weak">
                  {t("home.noCheckinsToVerify", "Nothing to verify")}
                </Text>
              </Box>
            ) : (
              <Box
                gap="xsmall"
                overflow={{ vertical: "auto" }}
                style={{ maxHeight: "60vh" }}
              >
                {verifyList.map((b) => (
                  <Box
                    key={b.id}
                    direction="row"
                    align="center"
                    gap="small"
                    pad="small"
                    round="xsmall"
                    flex={false}
                    onClick={() => openVerify(b)}
                    hoverIndicator={{ background: "#eaf1fb" }}
                    focusIndicator={false}
                    style={{ cursor: "pointer" }}
                  >
                    <IconBadge
                      Icon={DocumentUser}
                      color={BRAND}
                      tint={BRAND_TINT}
                      size="16px"
                    />
                    <Box flex>
                      <Text weight="bold" size="small" color="text-strong">
                        {b.guestName || t("guestDetails.guest", "Guest")}
                        {b.roomNumber
                          ? ` · ${t("dashboard.room", { number: b.roomNumber })}`
                          : ""}
                      </Text>
                      {b.checkinDate && (
                        <Text size="small" color="text-weak">
                          {new Date(b.checkinDate).toLocaleDateString(
                            undefined,
                            {
                              weekday: "short",
                              month: "short",
                              day: "numeric",
                            },
                          )}
                        </Text>
                      )}
                    </Box>
                    <LinkNext size="14px" color="text-xweak" />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Layer>
      )}

      {showTrainingPrompt && (
        <Layer
          onEsc={() => setShowTrainingPrompt(false)}
          onClickOutside={() => setShowTrainingPrompt(false)}
          responsive={false}
          margin="medium"
        >
          <Box
            width={{ width: "92vw", max: "420px" }}
            pad="medium"
            gap="medium"
          >
            <Box direction="row" align="center" justify="between">
              <Heading level={3} margin="none">
                {t("aiBrief.viewTrainingPromptTitle", "Training data added")}
              </Heading>
              <Button
                plain
                icon={<Close size="small" color="text-xweak" />}
                a11yTitle={t("common.close", "Close")}
                onClick={() => setShowTrainingPrompt(false)}
                hoverIndicator={{ background: "#e6e9ec" }}
                focusIndicator={false}
                style={{ padding: 6, borderRadius: 999 }}
              />
            </Box>
            <Text color="text-weak">
              {t(
                "aiBrief.viewTrainingPromptMessage",
                "Would you like to review it now and publish it to your AI assistant?",
              )}
            </Text>
            <Box direction="row" justify="end" gap="small">
              <Button
                label={t("aiBrief.viewTrainingPromptLater", "Later")}
                onClick={() => setShowTrainingPrompt(false)}
              />
              <Button
                primary
                label={t("aiBrief.viewTrainingPromptConfirm", "View & publish")}
                onClick={goToTrainingQnA}
              />
            </Box>
          </Box>
        </Layer>
      )}
    </Box>
  );
}
